import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Flex,
  Form,
  FormInstance,
  Modal,
  Skeleton,
  Tabs,
  Typography,
} from "antd";
import BackPageHeading from "components/BackPageHeading.component";
import { ModalHeader, modalButtonsTheme } from "components/modals/ModalChrome";
import { RestrictedBanner } from "components/capability";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useUpdateBusinessPolicies } from "hooks/mutations/business-settings";
import { useTabParam } from "hooks/useTabParam";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { BusinessSettings, UpdateBusinessPoliciesForm } from "shared/types";
import { colors } from "shared/theme/color";
import { getApiSuccessMessage } from "shared/utils";
import { textPx } from "shared/constants/zoom";
import {
  SettingsPage,
  SettingsSections,
  SettingsSectionsFooter,
} from "pages/settings/settingsRows";

const { Text } = Typography;

// The two panes a page with a history can show; the key rides ?tab=.
const POLICY_TABS = ["settings", "history"] as const;

// Shared chrome and save machinery for the three business-wide booking
// policies. Each policy is its own route off the Pengaturan launcher; before
// that revamp all three were stacked forms on /settings (PengaturanTab).
//
// Everything that is easy to get subtly wrong lives here exactly once: one-shot
// hydration, the dirty flag, the guarded validate, the no-op toast, and the
// before/after confirmation. A page supplies only its fields and its diff.

/** One row of the before/after confirmation summary. */
export type DiffRow = { label: string; before: string; after: string };

// A cutoff (value + unit) formats through fmtCutoff in shared/utils/cutoff.ts.

export const fmtBool = (b: boolean) =>
  b ? translate("class_settings.on") : translate("class_settings.off");

// What setFieldsValue actually accepts (rc-field-form's RecursivePartial).
// Derived from the method rather than written as Partial<V>, which TypeScript
// can't prove assignable while V is still generic.
type HydrationValues<V> = Parameters<FormInstance<V>["setFieldsValue"]>[0];

interface PolicySettingsPageProps<V> {
  /** Page heading, card context and the confirm modal's eyebrow. */
  title: string;
  /**
   * Muted line(s) above the fields. A node so a page can stack a second
   * line (the waitlist page's client-scope note); the client-scope note used
   * to be a page-level Alert and now lives beside the rule it qualifies.
   */
  subtitle?: ReactNode;
  /**
   * The Pembatasan Admin group this page's fields belong to. When the
   * signed-in Admin's Manager restricted it, the page renders read-only.
   * Omitted by the restriction page itself (Manager-only by route).
   */
  capability?: AdminCapability;
  /**
   * Owned by the page, not created here, so a page can call Form.useWatch at
   * its own top level to drive a conditional field.
   */
  form: FormInstance<V>;
  /** Which BusinessSettings fields this page hydrates. */
  initialValues: (settings: BusinessSettings) => HydrationValues<V>;
  /**
   * Validated values plus server state, to confirm rows and a PATCH body.
   * `notes` replaces `confirmNote` in the confirm pop-up when a page holds
   * several rules and only some changed (Kebijakan Booking & Jadwal).
   * `warnings` show as amber alerts above the notes, for a save that
   * removes something (switching the waitlist off).
   */
  buildSave: (
    values: V,
    settings: BusinessSettings,
  ) => {
    rows: DiffRow[];
    payload: UpdateBusinessPoliciesForm;
    notes?: string[];
    warnings?: string[];
  };
  /** The confirm pop-up's note, e.g. class_settings.confirm.note_live. */
  confirmNote?: string;
  /**
   * Optional extra async gate before the standard diff-confirm modal opens —
   * e.g. a conflict preview. Called with the built payload; MUST call
   * proceed() itself, immediately if there's nothing to warn about (the
   * default behavior for every page that doesn't pass this). Only Employee
   * Management uses this today (a business-wide reactivation-conflict scan);
   * the other five policy pages are unaffected.
   */
  beforeConfirm?: (payload: UpdateBusinessPoliciesForm, proceed: () => void) => void;
  /**
   * True while beforeConfirm's own check is in flight (not while any modal
   * it opens is being looked at) — folded into the save button's loading
   * state so it can't be double-clicked mid-check. Ignored if beforeConfirm
   * isn't passed.
   */
  beforeConfirmLoading?: boolean;
  /** The page's Form.Items. */
  children: ReactNode;
  /**
   * The page brings its own SettingsSection groups: no outer card, the
   * subtitle sits under the heading and the save footer under the groups.
   */
  sectioned?: boolean;
  /**
   * A second tab, "Riwayat Perubahan", beside the settings (the Tax page's
   * change history). Rendered only on a sectioned page, outside the Form
   * and the read-only DisabledContext, so it never affects `dirty` and
   * stays readable for a restricted Admin. The settings pane stays mounted
   * while the history is open, so an unsaved edit survives a tab switch.
   * The open tab rides the URL (?tab=history) like every other tabbed page.
   */
  historyTab?: ReactNode;
}

function PolicySettingsPage<V extends object>({
  title,
  subtitle,
  capability,
  form,
  initialValues,
  buildSave,
  confirmNote,
  beforeConfirm,
  beforeConfirmLoading,
  children,
  sectioned,
  historyTab,
}: PolicySettingsPageProps<V>) {
  const { data: settings, isLoading } = useBusinessSettings();
  const updatePolicies = useUpdateBusinessPolicies();
  const tab = useTabParam("settings", "tab", POLICY_TABS);
  // Pembatasan Admin: a plain Admin whose Manager restricted this page's
  // group still sees every value (the tiles stay navigable) but nothing
  // here can be changed: banner, DisabledContext over the fields, no save
  // footer. The heading's back arrow sits outside the disabled subtree.
  const { isRestricted } = useAdminCapabilities();
  const readOnly = capability != null && isRestricted(capability);

  // Derived, not a manually-toggled boolean: "dirty" means the live form
  // values actually differ from the last-saved settings right now, checked
  // via the same buildSave diff used for the real save. Form.useWatch reacts
  // to ANY change to the store, including a programmatic form.setFieldsValue
  // (e.g. beforeConfirm reverting a toggle after "Batal"), not just direct
  // user interaction — so the unsaved dot can't get stuck showing after a
  // revert puts the form back to matching settings, which a plain
  // onValuesChange-set boolean would (it only ever flips true, never back).
  const watchedValues = Form.useWatch([], form);
  const [pending, setPending] = useState<{
    rows: DiffRow[];
    payload: UpdateBusinessPoliciesForm;
    notes?: string[];
    warnings?: string[];
  } | null>(null);

  // Hydrate once, when settings first arrive. A saving mutation invalidates
  // ["business"], and the QueryClient sets no defaults so react-query's
  // refetchOnWindowFocus is live. Either can resolve mid-edit, and an
  // unguarded effect would silently overwrite what the admin is typing.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!settings || hydratedRef.current) return;
    hydratedRef.current = true;
    form.setFieldsValue(initialValues(settings));
  }, [settings, form, initialValues]);

  const heading = <BackPageHeading title={title} backTo={PATHS.SETTINGS} />;

  if (isLoading || !settings) {
    return (
      <>
        {heading}
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </>
    );
  }

  // Gated on hydratedRef too: on the very first render after settings loads,
  // watchedValues can still reflect the form's pre-hydration (empty) store —
  // the hydration effect above hasn't run yet — which would otherwise diff
  // as "changed" against real settings for any field whose default isn't
  // already off/undefined, showing the dot for one frame before the effect
  // corrects it. hydratedRef flips true synchronously inside that same
  // effect, before its own setFieldsValue call triggers the watch-driven
  // re-render this depends on, so by the render where it matters the guard
  // has already cleared.
  const dirty =
    hydratedRef.current &&
    !!watchedValues &&
    buildSave(watchedValues as V, settings).rows.length > 0;

  const onSave = async () => {
    // validateFields rejects on empty/invalid input. AntD has already painted
    // the inline field errors, so just bail. Catching the rejection is what
    // prevents the unhandled promise rejection that otherwise surfaced as a
    // full-page error overlay instead of the inline message.
    const values = await form.validateFields().catch((err) => {
      // On a long page the field in error can be off screen.
      const first = err?.errorFields?.[0]?.name;
      if (first) form.scrollToField(first, { block: "center", behavior: "smooth" });
      return null;
    });
    if (!values) return;

    const { rows, payload, notes, warnings } = buildSave(values, settings);
    // Changed then reverted: a toast, not a confirmation modal.
    if (rows.length === 0) {
      getApiSuccessMessage(translate("class_settings.toast.no_changes"));
      return;
    }
    const proceed = () => setPending({ rows, payload, notes, warnings });
    if (beforeConfirm) {
      beforeConfirm(payload, proceed);
    } else {
      proceed();
    }
  };

  const confirmSave = () => {
    if (!pending) return;
    // The success toast and every cache invalidation already live in
    // useUpdateBusinessPolicies. This only clears local state.
    updatePolicies.mutate(pending.payload, {
      // dirty is derived from settings vs the live form, so once the mutation's
      // own ["business"] invalidation lands fresh settings, it resolves back
      // to false on its own — only local pending-modal state needs clearing.
      onSuccess: () => setPending(null),
    });
  };

  const saveControls = (
    <>
      {dirty && (
        <span
          aria-label="unsaved"
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: colors.amber,
            display: "inline-block",
          }}
        />
      )}
      <Button
        type="primary"
        disabled={!dirty}
        loading={updatePolicies.isPending || !!beforeConfirmLoading}
        onClick={onSave}
      >
        {translate("button.save_changes")}
      </Button>
    </>
  );

  // A sectioned page (Notifikasi, Pembatasan Admin) brings its own groups:
  // the subtitle reads as the page description under the heading and Save
  // sits under the last group. Every other policy page is one card.
  const body = sectioned ? (
    <Flex vertical gap={16}>
      {subtitle && (
        <Text type="secondary" style={{ fontSize: textPx(14), marginTop: -8 }}>
          {subtitle}
        </Text>
      )}
      {readOnly && <RestrictedBanner />}
      <ConfigProvider componentDisabled={readOnly}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <SettingsSections>
            {children}
            {!readOnly && <SettingsSectionsFooter>{saveControls}</SettingsSectionsFooter>}
          </SettingsSections>
        </Form>
      </ConfigProvider>
    </Flex>
  ) : (
    <Flex vertical gap={16}>
      {readOnly && <RestrictedBanner />}

      <Card>
        {subtitle && <Text type="secondary">{subtitle}</Text>}

        <ConfigProvider componentDisabled={readOnly}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            style={subtitle ? { marginTop: 16 } : undefined}
          >
            {children}
          </Form>
        </ConfigProvider>

        {!readOnly && (
          <Flex justify="flex-end" align="center" gap={10} style={{ marginTop: 16 }}>
            {saveControls}
          </Flex>
        )}
      </Card>
    </Flex>
  );

  // With a history tab the heading and the tab bar run the page's full
  // width, like a client package's page; only the settings pane keeps the
  // sections' capped width (SettingsPage), so the history table can use the
  // whole page the way the package Riwayat tab does.
  const Frame = sectioned && !historyTab ? SettingsPage : Fragment;
  return (
    <Frame>
      {heading}
      {sectioned && historyTab ? (
        <Tabs
          activeKey={tab.activeKey}
          onChange={tab.onChange}
          size="small"
          items={[
            {
              key: "settings",
              label: translate("class_settings.tab.settings"),
              children: <SettingsPage>{body}</SettingsPage>,
            },
            { key: "history", label: translate("class_settings.history.title"), children: historyTab },
          ]}
        />
      ) : (
        body
      )}

      <ConfigProvider theme={modalButtonsTheme}>
        <Modal
          open={!!pending}
          onCancel={() => setPending(null)}
          onOk={confirmSave}
          okText={translate("button.save_changes")}
          cancelText={translate("button.cancel")}
          confirmLoading={updatePolicies.isPending}
          title={
            <ModalHeader
              eyebrow={title}
              title={translate("class_settings.confirm.title")}
              tone="amber"
            />
          }
        >
          <Flex vertical gap={10} style={{ marginTop: 20 }}>
            {pending?.rows.map((row) => (
              <Flex key={row.label} justify="space-between" gap={16}>
                <Text strong style={{ fontWeight: 700, color: colors.ink }}>
                  {row.label}
                </Text>
                <Text>
                  {row.before} → <strong>{row.after}</strong>
                </Text>
              </Flex>
            ))}
            {pending?.warnings?.map((warning) => (
              <Alert key={warning} type="warning" showIcon message={warning} style={{ marginTop: 6 }} />
            ))}
            {(pending?.notes ?? (confirmNote ? [confirmNote] : [])).map((note) => (
              <Text key={note} type="secondary" style={{ fontSize: textPx(12), marginTop: 4 }}>
                {note}
              </Text>
            ))}
          </Flex>
        </Modal>
      </ConfigProvider>
    </Frame>
  );
}

// The card title is gone: the page heading already names the policy, so
// repeating it inside would be noise. The unsaved dot moved to the save
// footer, matching LoyaltySettingsCard's full-page form.
export default PolicySettingsPage;
